import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Alert,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import * as Yup from "yup";
// @ts-ignore
import "react-toastify/dist/ReactToastify.css";
import { ActivityButton, SequenceNumberInput } from "../../components/Common";
import { CreateSubjectInstructionRequest } from "../../interfaces/subject-instruction";
import { ErrorHandlingTypeEnum, SequenceEnum } from "../../enums";
import {
  useGetSubjectInstructionQuery,
  useAddSubjectInstructionMutation,
  useUpdateSubjectInstructionMutation,
} from "../../store/api/subjectInstructionApi";
import { ParsedError } from "../../interfaces";

type TProps = {
  id?: string | null;
  subjectId: string;
  show: boolean;
  onCloseClick: (event: any) => void;
};
export const AddEditSubjectInstructionModal: React.FC<TProps> = ({
  id,
  subjectId,
  show,
  onCloseClick,
}) => {
  const { t } = useTranslation();
  const [request] = useState<CreateSubjectInstructionRequest>();

  const {
    data: dataInstruction,
    error: errorInstruction,
    isError: isErrorInstruction,
    isLoading: isLoadingInstruction,
  } = useGetSubjectInstructionQuery(id as string, { skip: !id });

  const [
    addSubjectInstruction,
    {
      isLoading: isLoadingAdd,
      error: errorAdd,
      isError: isErrorAdd,
      isSuccess: isSuccessAdd,
    },
  ] = useAddSubjectInstructionMutation();

  const [
    updateSubjectInstruction,
    {
      isLoading: isLoadingUpdate,
      error: errorUpdate,
      isError: isErrorUpdate,
      isSuccess: isSuccessUpdate,
    },
  ] = useUpdateSubjectInstructionMutation();

  //#region form
  const form = useFormik<CreateSubjectInstructionRequest>({
    enableReinitialize: true,
    initialValues: {
      title: {
        en: request?.title?.en || "",
        ar: request?.title?.ar || "",
      },
      sequenceNumber: request?.sequenceNumber || 0,
      subjectId: subjectId,
      instruction: {
        en: request?.instruction?.en || "",
        ar: request?.instruction?.ar || "",
      },
      isDefault: request?.isDefault || false,
    },
    validationSchema: Yup.object({
      title: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            }),
          ),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            }),
          ),
      }),
      instruction: Yup.object({
        en: Yup.string()
          .max(
            15000,
            t("maxLength", { maxLength: 15000, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            }),
          ),
        ar: Yup.string()
          .max(
            15000,
            t("maxLength", { maxLength: 15000, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            }),
          ),
      }),
      isFree: Yup.bool(),
    }),
    onSubmit: async (values) => {
      try {
        if (id) {
          await updateSubjectInstruction({
            id,
            data: { ...values, sequenceNumber: Number(values.sequenceNumber) },
          }).unwrap();
        } else {
          await addSubjectInstruction({
            ...values,
            sequenceNumber: Number(values.sequenceNumber),
          }).unwrap();
        }
        onClose();
      } catch (err) {
        console.error(err);
      }
    },
  });
  //#endregion

  //#region effects
  useEffect(() => {
    if (id && dataInstruction) {
      form.setValues({
        title: dataInstruction.title,
        instruction: dataInstruction.instruction,
        sequenceNumber:
          dataInstruction?.sequenceNumber || form.values.sequenceNumber || 0,
        subjectId: subjectId,
        isDefault: dataInstruction.isDefault,
      });
    }
  }, [dataInstruction, isLoadingInstruction, id]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingAdd && isErrorAdd) _error = errorAdd as ParsedError;
    if (!isLoadingUpdate && isErrorUpdate) _error = errorUpdate as ParsedError;
    if (_error?.type == ErrorHandlingTypeEnum.FORM) {
      form.setErrors(_error.error);
    }
  }, [isLoadingAdd, isLoadingUpdate]);

  useEffect(() => {
    if (
      (!isLoadingAdd && isSuccessAdd) ||
      (!isLoadingUpdate && isSuccessUpdate)
    ) {
      onClose();
    }
  }, [isLoadingAdd, isSuccessAdd, isLoadingUpdate, isSuccessUpdate]);

  //#endregion

  //#region handlers
  const onClose = () => {
    form.resetForm();
    onCloseClick(!show);
  };

  //#endregion

  return (
    <React.Fragment>
      <Modal
        id="addEdit"
        isOpen={show}
        toggle={() => {
          onClose();
        }}
        centered
        size="xl"
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalHeader
            className="p-3"
            toggle={() => {
              onClose();
            }}
          >
            {id
              ? t("editItem", { item: "instruction" })
              : t("addItem", { item: "instruction" })}
          </ModalHeader>
          <ModalBody>
            <SequenceNumberInput
              tabIndex={1}
              value={form.values.sequenceNumber}
              onChange={(e) => {
                e.preventDefault();
                const { value } = e.target;
                const regex =
                  /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                if (regex.test(value.toString())) {
                  form.setFieldValue("sequenceNumber", value);
                } else {
                  form.setFieldValue("sequenceNumber", "");
                }
              }}
              onBlur={form.handleBlur}
              invalid={
                form.touched.sequenceNumber && form.errors.sequenceNumber
                  ? true
                  : false
              }
              sequenceType={SequenceEnum.SUBJECT_SYSTEM_INSTRUCTION}
              onGenerateSequenceNumber={(v: number) => {
                form.setFieldValue("sequenceNumber", v);
              }}
              mode={id ? "edit" : "add"}
              request={{
                type: SequenceEnum.SUBJECT_SYSTEM_INSTRUCTION,
                subjectId: subjectId,
              }}
            />
            {form.touched.sequenceNumber && form.errors.sequenceNumber ? (
              <FormFeedback type="invalid">
                {form.errors.sequenceNumber as string}
              </FormFeedback>
            ) : null}
            <div className="mb-3">
              <label htmlFor="title.en" className="form-label">
                {t("titleEn")}
                <span className="text-danger">*</span>
              </label>
              <Input
                disabled={isErrorInstruction}
                autoComplete="off"
                id="title.en"
                name="title.en"
                value={form.values?.title.en}
                type="text"
                className="form-control"
                placeholder={t("enterInEnglish")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.title?.en && form.errors.title?.en ? true : false
                }
              />
              {form.touched.title?.en && form.errors.title?.en ? (
                <FormFeedback type="invalid">
                  {form.errors.title?.en as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="title.ar" className="form-label">
                {t("titleAr")}
                <span className="text-danger">*</span>
              </label>
              <Input
                style={{ fontFamily: "aljannat", direction: "rtl" }}
                disabled={isErrorInstruction}
                autoComplete="off"
                auto
                id="title.ar"
                name="title.ar"
                value={form.values.title?.ar}
                type="text"
                className="form-control"
                placeholder={t("enterInArabic")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.title?.ar && form.errors.title?.ar ? true : false
                }
              />
              {form.touched.title?.ar && form.errors.title?.ar ? (
                <FormFeedback type="invalid">
                  {form.errors.title?.ar as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="instruction.en" className="form-label">
                {t("instructionEn")}
                <span className="text-danger">*</span>
              </label>
              <Input
                disabled={isErrorInstruction}
                autoComplete="off"
                id="instruction.en"
                name="instruction.en"
                value={form.values?.instruction.en}
                type="textarea"
                className="form-control"
                placeholder={t("enterInEnglish")}
                onChange={form.handleChange}
                rows={15}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.instruction?.en && form.errors.instruction?.en
                    ? true
                    : false
                }
              />
              {form.touched.instruction?.en && form.errors.instruction?.en ? (
                <FormFeedback type="invalid">
                  {form.errors.instruction?.en as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="instruction.ar" className="form-label">
                {t("instructionAr")}
                <span className="text-danger">*</span>
              </label>
              <Input
                style={{ fontFamily: "aljannat", direction: "rtl" }}
                disabled={isErrorInstruction}
                autoComplete="off"
                auto
                id="instruction.ar"
                name="instruction.ar"
                value={form.values.instruction?.ar}
                type="textarea"
                rows={15}
                className="form-control"
                placeholder={t("enterInArabic")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.instruction?.ar && form.errors.instruction?.ar
                    ? true
                    : false
                }
              />
              {form.touched.instruction?.ar && form.errors.instruction?.ar ? (
                <FormFeedback type="invalid">
                  {form.errors.instruction?.ar as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <Col lg={4}>
                <div>
                  <div className="form-check mb-4 mt-2">
                    <Input
                      className="form-check-input"
                      type="checkbox"
                      id="isDefault"
                      checked={form.values.isDefault}
                      value={(form.values.isDefault || false) as any}
                      onChange={form.handleChange}
                      onBlur={form.handleBlur}
                    />
                    <Label className="form-check-label" htmlFor="isDefault">
                      {t("markAsDefault")}
                    </Label>
                  </div>
                </div>
              </Col>
            </div>
          </ModalBody>
          <ModalFooter>
            <Link
              to={"#"}
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </Link>
            <ActivityButton
              title="save"
              inProgressTitle="saving"
              color="success"
              className="btn btn-success"
              type="submit"
              inProgress={isLoadingAdd || isLoadingUpdate}
              disabled={isLoadingAdd || isLoadingUpdate || isErrorInstruction}
            ></ActivityButton>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};
